import { UBAppModal, UBRadioBox } from "../../../../Libraries";
import { Box } from "@mui/material";
import StockInOutList from "./StockInOutList";
import UBInput from "../../../../Libraries/UBInput";
import { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { bulkStockUpdation } from "../../../../database/helpers/stock";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { useAllStockReport } from "../../../../hooks/useAllStockReport";
import { Sync } from "../../../../SyncService";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { SuccessIcon } from "../../../../assets/icons/icon";
import { useAuthContext } from "../../../../context/AuthContext";
import styles from "./StockInOutModal.module.css";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useNavigate } from "react-router-dom";
import { InputSearchIcon } from "../../../../assets/icons/icon";
import { ITEMS_PER_PAGE } from "../../../../constants";

const StockInOutModal = ({
  showStockInModal,
  setShowStockInModal,
  item,
  handleSuccessCallBack = () => {},
}) => {
  const navigate = useNavigate();
  const { business, user } = useAuthContext();
  const [I18n] = useContext(LanguageContext);
  const [tabValue, setTabVal] = useState("stockin"); // UBAppTabs state
  const [query, setQuery] = useState("");
  const [paramsMap, setParamsMap] = useState({});
  const [enableSave, setEnableSave] = useState(
    Object.keys(paramsMap).length !== 0,
  );
  const [page, setPage] = useState(0);

  const type = tabValue === "stockin" ? "in" : "out"; // decide type based on UBAppTabs
  const debounceSearchQuery = useDebouncedValue(query, 500);
  const { showSnackbar } = useSnackbar();

  const { stockList, handleStock, totalStockCount,totalAllStocks, isLoading } =
    useAllStockReport(business, debounceSearchQuery);

  useEffect(() => {
    if (showStockInModal && !item) triggerLogEvent(analytics, "sb_io_btn");
  }, [showStockInModal]);

  const handleGetInitialData = () => {
    setPage(0);
    handleStock({
      limit: ITEMS_PER_PAGE,
      offset: 0,
      filter: {
        keywords: debounceSearchQuery,
      },
    });
  };

  const handleGetMoreData = () => {
    setPage((prev) => {
      handleStock({
        limit: ITEMS_PER_PAGE,
        offset: (prev + 1) * ITEMS_PER_PAGE,
        filter: {
          keywords: debounceSearchQuery,
        },
        isCalculateTotal: false,
      });
      return prev + 1;
    });
  };

  useEffect(() => {
    setEnableSave(
      Object.values(paramsMap).find((item) => item.stock && item.stock !== "0"),
    );
  }, [paramsMap]);

  useEffect(() => {
    const myDiv = document.getElementById("StockInOutScrollableDiv");
    if (myDiv) myDiv.scrollTop = 0;
    handleGetInitialData();
  }, [debounceSearchQuery]);

  const handleStockModalClose = async () => {
    setShowStockInModal(false);
    setParamsMap({});
  };
  const handleSave = async () => {
    if (!enableSave) {
      return;
    }
    const stock_items = [];
    try {
      for (const params of Object.values(paramsMap)) {
        const current_quantity = Number(params.item_current_quantity) || 0;
        const unit_conversion = params.unit_conversion;
        const quantity = Number(params?.stock || 0);
        let paramsForUpdate = {
          item_id: params.item_id,
          stock:
            params.type === "in"
              ? current_quantity + quantity
              : current_quantity - quantity,
          is_secondary_unit: params.isSecondary,
          unit_conversion: unit_conversion,
          quantity: quantity,
          price: Number(params?.amount || 0),
          transacted_at: params.date,
          type: params.type,
          name: params.name,
        };

        if (params.isSecondary) {
          const secondaryQuantity = quantity / Number(unit_conversion || 1);
          paramsForUpdate.stock =
            params.type === "in"
              ? current_quantity + secondaryQuantity
              : current_quantity - secondaryQuantity;
        }
        if (quantity) stock_items.push(paramsForUpdate);
      }
      await bulkStockUpdation(stock_items);

      //  Added Firebase Analytics Logs
      triggerLogEvent(
        analytics,
        type === "in" ? "sb_io_jn_save" : "sb_io_out_save",
        {
          total_items: stock_items.length,
          user: user?.phone_number || "unknown",
        },
      );
      handleStockModalClose();
      Sync(user.phone_number, navigate);
      handleSuccessCallBack();
      showSnackbar({
        message: type === "in" ? I18n.stock_in_updated : I18n.stock_out_updated,
        severity: "success",
      });
    } catch (error) {
      console.error(`Stock IN/OUT error: ${error}`);
    }
  };

  return (
    <UBAppModal
      id="stock_in/out"
      className={styles.stockInOutModal}
      size={"lg"}
      disableContainerScroll
      open={showStockInModal}
      header={{
        title: "stock_in_out",
        handleClose: handleStockModalClose,
        dont_show_cross_icon: true,
      }}
      footer={[
        {
          data: {
            className: `ubSecondaryButton`,
            title: `${I18n.cancel}`,
            onClick: () => handleStockModalClose(),
            disabled: false,
            loading: false,
            id: "stock_in/out_cancel",
          },
        },
        {
          data: {
            className: `ubPrimaryButton`,
            title: `${I18n.save}`,
            onClick: () => handleSave(),
            disabled: enableSave ? false : true,
            loading: false,
            id: "stock_in/out_save",
          },
        },
      ]}
    >
      <Box className={styles.modalBodyContent}>
        <Box className={styles.modalBodyTop}>
          <Box className="accountFilterModalBody">
            <Box className={styles.radioBoxWrapper}>
              <UBRadioBox
                label={I18n.stock_in}
                checked={tabValue === "stockin"}
                onChange={() => {
                  setTabVal("stockin");
                  handleGetInitialData();
                }}
                value={tabValue}
              />
              <UBRadioBox
                label={I18n.stock_out}
                checked={tabValue === "stockout"}
                onChange={() => {
                  setTabVal("stockout");
                  handleGetInitialData();
                }}
                value={tabValue}
              />
            </Box>
          </Box>

          {!item ? (
            <UBInput
              inputId="stock_in_out_modal_search"
              name="search"
              label=""
              value={query}
              placeholder={I18n.search_items}
              onChange={(name, text) => setQuery(text)}
              inputClassname={styles.searchInput}
              baseClassname={styles.searchInputBase}
              prefix={<InputSearchIcon />}
              onFocus={() => triggerLogEvent(analytics, "sb_io_search")}
            />
          ) : (
            <Box marginBottom={2} />
          )}
        </Box>

        <StockInOutList
          className={"stockinoutlist"}
          paramsMap={paramsMap}
          setParamsMap={setParamsMap}
          data={!item ? stockList : [item]}
          type={type}
          totalStockCount={totalStockCount}
          totalAllStocks={totalAllStocks}
          isLoading={isLoading}
          isSingleStock={!!item}
          handleGetMoreData={handleGetMoreData}
        />
      </Box>
    </UBAppModal>
  );
};

export default StockInOutModal;
